import React, { useEffect, useState, useMemo } from 'react';
import { Dialog } from '@mui/material';
import { toast } from 'react-toastify';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import {
  X,
  Receipt,
  Building2,
  Wallet,
  PackageCheck,
  TrendingUp,
  Lock,
  Copy,
  CheckCircle2,
  Save,
  Check,
} from 'lucide-react';

const ViewRemittance = ({ isopen, onClose, data }) => {
  const { isDarkMode } = useMyContext();
  const [orderData, setOrderData] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [copiedText, setCopiedText] = useState('');

  useEffect(() => {
    if (data && data.order_id) {
      try {
        const parsedOrders = typeof data.order_id === 'string'
          ? JSON.parse(data.order_id)
          : Array.isArray(data.order_id)
            ? data.order_id
            : [];
        setOrderData(parsedOrders);

        const initialFormData = {};
        parsedOrders.forEach((order) => {
          initialFormData[order.order_id] = String(order.amount ?? '0');
        });
        setFormData(initialFormData);
      } catch (error) {
        console.error('Error parsing order data:', error);
        setOrderData([]);
      }
    } else {
      setOrderData([]);
      setFormData({});
    }
  }, [data]);

  const handleClose = () => {
    setErrors({});
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const copyToClipboard = (text, label = 'Copied') => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    setCopiedText(String(text));
    toast.success(`${label} copied!`, { autoClose: 1200 });
    setTimeout(() => setCopiedText(''), 1500);
  };

  const handleAmountChange = (orderId, value) => {
    setFormData((prev) => ({
      ...prev,
      [orderId]: value,
    }));

    if (errors[orderId]) {
      setErrors((prev) => ({
        ...prev,
        [orderId]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((orderId) => {
      const amount = formData[orderId];
      if (!amount || String(amount).trim() === '') {
        newErrors[orderId] = 'Required';
      } else if (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        newErrors[orderId] = 'Invalid';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      toast.success('Settlement changes saved!');
      handleClose();
    }
  };

  const calculatedTotal = useMemo(() => {
    return Object.values(formData).reduce((total, amount) => {
      return total + (parseFloat(amount) || 0);
    }, 0);
  }, [formData]);

  if (!data) return null;

  const isPending = (data.status || '').toLowerCase() === 'pending';
  const statusLower = (data.status || 'pending').toLowerCase();
  const settleId = data.settle_id || data.id || '—';
  const retailerId = data.retailer_id ?? data.user_id ?? data.cus_id ?? '—';
  const settlementAmount = parseFloat(data.remaining_amount || data.amount || 0);

  const getStatusBadge = () => {
    if (statusLower === 'processed' || statusLower === 'completed') {
      return {
        bg: isDarkMode ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
        dot: 'bg-emerald-400',
        label: statusLower === 'processed' ? 'Processed' : 'Completed',
      };
    }
    if (statusLower === 'pending') {
      return {
        bg: isDarkMode ? 'bg-amber-500/10 text-amber-300 border-amber-500/30' : 'bg-amber-50 text-amber-700 border-amber-200',
        dot: 'bg-amber-400',
        label: 'Pending',
      };
    }
    return {
      bg: isDarkMode ? 'bg-gray-500/10 text-gray-300 border-gray-500/30' : 'bg-gray-100 text-gray-700 border-gray-200',
      dot: 'bg-gray-400',
      label: data.status || 'Unknown',
    };
  };

  const statusBadge = getStatusBadge();

  return (
    <Dialog
      open={Boolean(isopen)}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '1.25rem',
          overflow: 'hidden',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
          boxShadow: isDarkMode
            ? '0 25px 60px -15px rgba(0, 0, 0, 0.7), 0 0 40px rgba(99, 102, 241, 0.15)'
            : '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
          background: isDarkMode ? '#0F172A' : '#FFFFFF',
          color: isDarkMode ? '#F1F5F9' : '#0F172A',
        },
      }}
    >
      <div className={`flex flex-col max-h-[90vh] ${isDarkMode ? 'bg-[#0F172A] text-slate-100' : 'bg-white text-slate-900'}`}>
        {/* ================= MODAL HEADER ================= */}
        <div
          className={`relative px-6 py-4 border-b flex items-center justify-between shrink-0 transition-colors ${
            isDarkMode
              ? 'bg-gradient-to-r from-slate-900 via-indigo-950/40 to-slate-900 border-slate-800'
              : 'bg-gradient-to-r from-slate-50 via-indigo-50/30 to-slate-50 border-slate-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${
                isDarkMode
                  ? 'bg-gradient-to-tr from-indigo-600 to-violet-600 text-white shadow-indigo-500/25'
                  : 'bg-gradient-to-tr from-indigo-500 to-indigo-600 text-white shadow-indigo-500/20'
              }`}
            >
              <Receipt size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold leading-tight tracking-tight">
                  Remittance Settlement Details
                </h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${statusBadge.bg}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${statusBadge.dot} animate-pulse`} />
                  {statusBadge.label}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5 text-xs flex-wrap">
                <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                  Master No:
                </span>
                <span className="font-mono font-semibold text-indigo-500 dark:text-indigo-400 flex items-center gap-1">
                  {settleId}
                  <button
                    onClick={() => copyToClipboard(settleId, 'Master No')}
                    className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                    title="Copy Master Settlement ID"
                  >
                    {copiedText === settleId ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                  </button>
                </span>
                {data?.tnx_id && (
                  <>
                    <span className={isDarkMode ? 'text-slate-600' : 'text-slate-300'}>•</span>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                      UTR ID:
                    </span>
                    <span className="font-mono font-semibold text-emerald-500 dark:text-emerald-400 flex items-center gap-1">
                      {data.tnx_id}
                      <button
                        onClick={() => copyToClipboard(data.tnx_id, 'UTR ID')}
                        className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors"
                        title="Copy UTR ID"
                      >
                        {copiedText === data.tnx_id ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                      </button>
                    </span>
                  </>
                )}
              </div>
              {(data?.bank_name || data?.account_no) && (
                <div className={`flex items-center gap-1.5 mt-1 text-[11px] ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  <Building2 size={12} className="text-indigo-400 shrink-0" />
                  <span>Target Bank: <strong className={isDarkMode ? 'text-slate-200' : 'text-slate-800'}>{data.bank_name || 'Bank'}</strong></span>
                  <span>•</span>
                  <span className="font-mono">A/C: <strong className={isDarkMode ? 'text-slate-200' : 'text-slate-800'}>{data.account_no}</strong></span>
                  {data.ifsc && (
                    <>
                      <span>•</span>
                      <span className="font-mono">IFSC: <strong className={isDarkMode ? 'text-slate-200' : 'text-slate-800'}>{data.ifsc}</strong></span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleClose}
            className={`p-1.5 rounded-lg border transition-all duration-150 ${
              isDarkMode
                ? 'border-slate-800 bg-slate-800/60 text-slate-400 hover:text-white hover:bg-slate-800 hover:border-slate-700'
                : 'border-slate-200 bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* ================= MODAL BODY ================= */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-5">
          {/* 4 Sleek Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Card 1: Retailer */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                isDarkMode
                  ? 'bg-slate-900/70 border-slate-800/90 shadow-sm'
                  : 'bg-slate-50/80 border-slate-200 shadow-xs'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Retailer
                </span>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-indigo-500/15 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                  <Building2 size={14} />
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <span className="text-base font-bold font-mono text-indigo-500 dark:text-indigo-400">
                  #{retailerId}
                </span>
              </div>
              <p className={`text-[10px] mt-0.5 truncate ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {data.retailer_name || `Retailer Account`}
              </p>
            </div>

            {/* Card 2: Settlement Amount */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                isDarkMode
                  ? 'bg-slate-900/70 border-slate-800/90 shadow-sm'
                  : 'bg-slate-50/80 border-slate-200 shadow-xs'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Settlement
                </span>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-emerald-500/15 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}>
                  <Wallet size={14} />
                </div>
              </div>
              <div className="text-base font-bold text-emerald-500 dark:text-emerald-400">
                ₹{settlementAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Disbursed to Bank
              </p>
            </div>

            {/* Card 3: Total Orders */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                isDarkMode
                  ? 'bg-slate-900/70 border-slate-800/90 shadow-sm'
                  : 'bg-slate-50/80 border-slate-200 shadow-xs'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Orders
                </span>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-blue-500/15 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                  <PackageCheck size={14} />
                </div>
              </div>
              <div className="text-base font-bold">
                {orderData.length}
                <span className={`text-xs font-normal ml-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  items
                </span>
              </div>
              <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                In this settlement batch
              </p>
            </div>

            {/* Card 4: Calculated Total */}
            <div
              className={`p-3.5 rounded-xl border transition-all ${
                isDarkMode
                  ? 'bg-slate-900/70 border-slate-800/90 shadow-sm'
                  : 'bg-slate-50/80 border-slate-200 shadow-xs'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase font-bold tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Batch Sum
                </span>
                <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-purple-500/15 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                  <TrendingUp size={14} />
                </div>
              </div>
              <div className="text-base font-bold text-indigo-500 dark:text-indigo-400">
                ₹{calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </div>
              <p className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Sum of itemized amounts
              </p>
            </div>
          </div>

          {/* Status Alert Banner */}
          {!isPending ? (
            <div
              className={`flex items-start gap-2.5 p-3 rounded-xl border text-xs transition-colors ${
                isDarkMode
                  ? 'bg-amber-950/25 border-amber-500/30 text-amber-200/90'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
            >
              <Lock size={15} className="text-amber-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Settlement Locked:</span> Amount editing is disabled because this
                settlement has already been{' '}
                <span className="capitalize font-semibold">{statusLower}</span>. Only pending settlements can be modified.
              </div>
            </div>
          ) : (
            <div
              className={`flex items-start gap-2.5 p-3 rounded-xl border text-xs transition-colors ${
                isDarkMode
                  ? 'bg-emerald-950/25 border-emerald-500/30 text-emerald-200/90'
                  : 'bg-emerald-50 border-emerald-200 text-emerald-800'
              }`}
            >
              <CheckCircle2 size={15} className="text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold">Pending Settlement:</span> You can adjust individual order amounts below
                before finalizing the payment processing.
              </div>
            </div>
          )}

          {/* Table Container */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                <span>Itemized Orders ({orderData.length})</span>
              </h3>
              <span className={`text-[11px] font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Total: ₹{calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            </div>

            <div
              className={`rounded-xl border overflow-hidden transition-colors ${
                isDarkMode ? 'border-slate-800 bg-slate-900/60 shadow-sm' : 'border-slate-200 bg-white shadow-xs'
              }`}
            >
              <div className="max-h-64 overflow-y-auto custom-scrollbar">
                <table className="w-full text-left border-collapse text-xs">
                  <thead
                    className={`sticky top-0 z-10 transition-colors ${
                      isDarkMode ? 'bg-slate-800/90 text-slate-300 border-b border-slate-700/80' : 'bg-slate-100 text-slate-600 border-b border-slate-200'
                    }`}
                  >
                    <tr>
                      <th className="py-2.5 px-3.5 text-[10px] font-bold uppercase tracking-wider w-12 text-center">#</th>
                      <th className="py-2.5 px-3.5 text-[10px] font-bold uppercase tracking-wider">Order ID</th>
                      <th className="py-2.5 px-3.5 text-[10px] font-bold uppercase tracking-wider text-right">Settled Amount</th>
                      <th className="py-2.5 px-3.5 text-[10px] font-bold uppercase tracking-wider text-center w-28">Status</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-slate-800/80' : 'divide-slate-100'}`}>
                    {orderData.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-slate-400 text-xs">
                          No order records attached to this settlement.
                        </td>
                      </tr>
                    ) : (
                      orderData.map((order, index) => {
                        const orderId = String(order.order_id || '');
                        const hasError = Boolean(errors[orderId]);
                        return (
                          <tr
                            key={orderId || index}
                            className={`transition-colors ${
                              isDarkMode
                                ? index % 2 === 0
                                  ? 'bg-slate-900/40 hover:bg-slate-800/60'
                                  : 'bg-slate-900/80 hover:bg-slate-800/60'
                                : index % 2 === 0
                                  ? 'bg-white hover:bg-slate-50'
                                  : 'bg-slate-50/40 hover:bg-slate-50'
                            }`}
                          >
                            {/* 1. Index */}
                            <td className="py-2.5 px-3.5 text-center text-slate-400 font-medium">
                              <span
                                className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                                  isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-200 text-slate-700'
                                }`}
                              >
                                {index + 1}
                              </span>
                            </td>

                            {/* 2. Order ID with copy */}
                            <td className="py-2.5 px-3.5">
                              <div className="flex items-center gap-1.5">
                                <span
                                  onClick={() => copyToClipboard(orderId, 'Order ID')}
                                  className="font-mono font-semibold text-indigo-500 dark:text-indigo-400 hover:underline cursor-pointer"
                                >
                                  {orderId}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(orderId, 'Order ID')}
                                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                  title="Copy Order ID"
                                >
                                  {copiedText === orderId ? (
                                    <Check size={11} className="text-emerald-500" />
                                  ) : (
                                    <Copy size={11} />
                                  )}
                                </button>
                              </div>
                            </td>

                            {/* 3. Amount */}
                            <td className="py-2.5 px-3.5 text-right font-medium">
                              {isPending ? (
                                <div className="inline-flex items-center justify-end">
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={formData[orderId] || ''}
                                      onChange={(e) => handleAmountChange(orderId, e.target.value)}
                                      className={`w-28 pl-5 pr-2 py-1 text-right text-xs font-semibold rounded-lg border outline-none transition-all ${
                                        hasError
                                          ? 'border-rose-500 text-rose-500 bg-rose-500/10'
                                          : isDarkMode
                                            ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500'
                                            : 'bg-white border-slate-300 text-slate-900 focus:border-indigo-500'
                                      }`}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <span className="font-bold text-slate-800 dark:text-slate-200">
                                  ₹{parseFloat(formData[orderId] || order.amount || 0).toLocaleString('en-IN', {
                                    minimumFractionDigits: 2,
                                  })}
                                </span>
                              )}
                            </td>

                            {/* 4. Status */}
                            <td className="py-2.5 px-3.5 text-center">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${statusBadge.bg}`}
                              >
                                {statusBadge.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {/* Table Footer with Summary */}
                  {orderData.length > 0 && (
                    <tfoot
                      className={`border-t font-bold transition-colors ${
                        isDarkMode ? 'border-slate-700 bg-slate-800/80 text-slate-200' : 'border-slate-200 bg-slate-100 text-slate-800'
                      }`}
                    >
                      <tr>
                        <td colSpan={2} className="py-2.5 px-3.5 text-left text-xs uppercase tracking-wider font-bold">
                          Total Batch Value
                        </td>
                        <td className="py-2.5 px-3.5 text-right font-extrabold text-sm text-indigo-500 dark:text-indigo-400">
                          ₹{calculatedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-3.5 text-center text-[10px] uppercase font-semibold text-slate-400">
                          Final
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ================= MODAL ACTIONS ================= */}
        <div
          className={`px-6 py-3.5 border-t flex items-center justify-between shrink-0 transition-colors ${
            isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-slate-50 border-slate-200'
          }`}
        >
          <div className="text-[11px] text-slate-400">
            {orderData.length} order{orderData.length !== 1 ? 's' : ''} in batch
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                  : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Close
            </button>

            {isPending ? (
              <button
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 shadow-sm transition-all"
              >
                <Save size={13} />
                <span>Save Changes</span>
              </button>
            ) : (
              <button
                disabled
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold cursor-not-allowed opacity-60 border ${
                  isDarkMode
                    ? 'bg-slate-800 border-slate-700 text-slate-400'
                    : 'bg-slate-200 border-slate-300 text-slate-500'
                }`}
                title="Only pending settlements can be edited"
              >
                <Lock size={12} />
                <span>Locked ({statusBadge.label})</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ViewRemittance;